import { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { IKnmSearchClipped } from 'ApiPackage/SearchModule/interfaces/IKnmSearchClipped';
import { ROUTE_DECISION, ROUTE_KNM } from 'CorePackage/MainModule/constants/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { getSearchResultsData, getSearchTotal, getSearchPage, getSearchTotalPages } from '../selectors/searchSelectors';
import styles from './SearchResults.module.scss';
import { ITableColumn } from '../interfaces/ITableColumn';

interface ISearchResultsStoreProps {
  data: IKnmSearchClipped[] | null;
  total: number;
  page: number;
  totalPages: number;
}

interface ISearchResultsProps {
  onLoadClick: () => void;
  columns: ITableColumn[];
  onSortChange: (id: string) => void;
}

const SearchResultsWithoutStore = ({
  data,
  total,
  onLoadClick,
  page,
  totalPages,
  columns,
  onSortChange,
}: ISearchResultsProps & ISearchResultsStoreProps) => {
  const handleSortClick = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onSortChange(id);
  };

  const getSortIcon = (id: string) => {
    const sort = columns.find(item => item.id === id)?.sort || '';
    if (sort === 'asc') {
      return '\xa0↑';
    }
    if (sort === 'desc') {
      return '\xa0↓';
    }
    return '';
  };

  const getJsonValue = (value: string) => {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  return (
    <div className={styles.SearchResults}>
      {data && <h3 className={styles.Title}>Найдено {total} КНМ</h3>}

      {data && data.length > 0 && (
        <div className={styles.Items}>
          {data.map(el => {
            const frguOrgFullName = getJsonValue(el.frguOrgName || el.frguOrgFullName);
            return (
              <div className={styles.Item} key={`${el.decision ? 'decision' : 'knm'}-${el.id}`}>
                <div className={styles.ItemHeader}>{el.id}</div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>УЧЕТНЫЙ НОМЕР КНМ</span>
                  <span className={styles.ItemParameterValue}>
                    <Link to={el.decision ? `${ROUTE_DECISION}/${el.id}` : `${ROUTE_KNM}/${el.id}`} target="_blank">
                      {el.number}
                    </Link>
                  </span>
                </div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>Федеральный закон</span>
                  <span className={styles.ItemParameterValue}>
                    {el.decision ? el.federalLaw || '-' : `${el.federalLaw}-ФЗ`}
                  </span>
                </div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>ИНН</span>
                  <span className={styles.ItemParameterValue}>{el.inn}</span>
                </div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>ОГРН</span>
                  <span className={styles.ItemParameterValue}>{el.ogrn}</span>
                </div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>НАИМЕНОВАНИЕ ПРОВЕРЯЕМОГО ЛИЦА</span>
                  <span className={styles.ItemParameterValue}>{el.faceName}</span>
                </div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>МЕСЯЦ НАЧАЛА ПРОВЕДЕНИЯ КНМ</span>
                  <span className={styles.ItemParameterValue}>{el.startMonth}</span>
                </div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>
                    НАИМЕНОВАНИЕ ОРГАНА ГОСУДАРСТВЕННОГО КОНТРОЛЯ (НАДЗОРА)
                  </span>
                  <span className={styles.ItemParameterValue}>
                    {typeof frguOrgFullName === 'object' && frguOrgFullName
                      ? frguOrgFullName.fullName
                      : frguOrgFullName}
                  </span>
                </div>
                <div className={styles.ItemParameter}>
                  <span className={styles.ItemParameterName}>НАИМЕНОВАНИЕ ОРГАНА ПРОКУРАТУРЫ</span>
                  <span className={styles.ItemParameterValue}>{el.prosecName}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <table className={styles.Table}>
        <thead className={styles.tHead}>
          <tr>
            {columns.map(column => (
              <th key={column.id} className={styles.TheadCell}>
                {column.isSortable && (
                  <a href="/" onClick={handleSortClick(column.id)}>
                    {column.name}
                    {getSortIcon(column.id)}
                  </a>
                )}
                {!column.isSortable && <span>{column.name}</span>}
              </th>
            ))}
          </tr>
        </thead>
        {data && data.length > 0 && (
          <tbody className={styles.TableSearchData}>
            {data.map(el => {
              const frguOrgFullName = getJsonValue(el.frguOrgName || el.frguOrgFullName);
              return (
                <tr key={`${el.decision ? 'decision' : 'knm'}-${el.id}`}>
                  <td>
                    <Link to={el.decision ? `${ROUTE_DECISION}/${el.id}` : `${ROUTE_KNM}/${el.id}`} target="_blank">
                      {el.number}
                    </Link>
                  </td>
                  <td>{el.decision ? el.federalLaw || '-' : `${el.federalLaw}-ФЗ`}</td>
                  <td>{el.inn}</td>
                  <td>{el.ogrn}</td>
                  <td>{el.faceName}</td>
                  <td>{el.startMonth}</td>
                  <td>
                    {typeof frguOrgFullName === 'object' && frguOrgFullName
                      ? frguOrgFullName.fullName
                      : frguOrgFullName}
                  </td>
                  <td>{el.prosecName}</td>
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
      {data && data.length > 0 && page !== totalPages && (
        <button className={styles.LoadButton} type="button" onClick={onLoadClick}>
          Показать еще
        </button>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  data: getSearchResultsData(state),
  total: getSearchTotal(state),
  page: getSearchPage(state),
  totalPages: getSearchTotalPages(state),
});

export const SearchResults = connect(mapStateToProps)(SearchResultsWithoutStore);
